import React, { useEffect, useRef, useState } from "react";
import { ArrowDown, Megaphone } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { showNativeAd, hideNativeAd, setNativeAdVisible, moveNativeAd, probeNativeAd, nativeAdOwner, nativeAdVisible, nativeScrollY, reportNativeSlotStuck, NATIVE_MEDIUM_HEIGHT, admobQaMode } from "@/lib/admob";
import { scrollParent } from "@/lib/nativeAdDrag";

// Emplacements d'annonces NATIVES Google (app native, via lib/admob.js). Le SDK dessine la vue native
// PAR-DESSUS la WebView, aux coordonnées du cadre `ref`.
// PRÉCHARGEMENT : dès que le cadre approche (~1 écran, PRELOAD_MARGIN) l'annonce est chargée et posée
// INVISIBLE ; quand le cadre est visible (≥ VISIBLE_RATIO, non recouvert, `enabled`, position stabilisée)
// elle est RÉVÉLÉE instantanément (aucune nouvelle demande). Cachée (pas détruite) quand le cadre sort
// un peu de l'écran ou que `enabled` tombe (reel plus actif) ; détruite seulement quand le cadre s'éloigne
// (au-delà de la marge), à la fermeture de la page ou au démontage. Une seule vue native à la fois
// (contrainte du SDK) : un cadre VISIBLE sans annonce la demande visible d'un coup et prend la vue d'un
// emplacement qui ne la garde que cachée ; l'évincé redemande quand il redevient visible. Une vue obtenue
// après que le cadre est parti loin (ou démonté) est aussitôt rendue — jamais de vue orpheline.
// state : "idle" | "loading" | "loaded" (prête, invisible) | "shown" | "failed"
let slotSeq = 0;
const VISIBLE_RATIO = 0.6;
// Reels : la slide défile dans un conteneur interne (la vue native ne suit pas ce défilement) → révélée
// seulement quand elle est (presque) entièrement en place, cachée dès que le glissement commence.
const REELS_VISIBLE_RATIO = 0.95;
const SHOW_DELAY_MS = 250;
const GUARD_MS = 400;
const SETTLE_MS = 90;          // le cadre doit être immobile entre deux lectures avant de révéler
const BUSY_RETRY_MS = 1500;    // un autre emplacement tient la vue → on réessaie plus tard
const STUCK_REPORT_MS = 8000;  // visible depuis 8 s sans annonce → remonté à l'admin (diagnostic)
const PRELOAD_MARGIN = "120% 0px 120% 0px";
const PROBE_DELAY_MS = 1000;    // sonde : 1 s après la révélation, la vue native est-elle vraiment là où est le cadre ?

const reportNativeStuck = (placement, s) => reportNativeSlotStuck(placement, `visible sans annonce · vue tenue par ${nativeAdOwner() === null ? "personne" : `l'emplacement ${String(nativeAdOwner())}`}${nativeAdVisible() ? " (visible)" : ""} · busy=${s.busy} evicted=${s.evicted} timer=${!!s.timer}`);

export function useAdmobNativeSlot({ placement, enabled = true, onUnavailable }) {
  const { config, user } = useApp();
  const ref = useRef(null);
  const [owner] = useState(() => ++slotSeq);
  const [state, setState] = useState("idle");
  const st = useRef({ near: false, visible: false, visibleSince: 0, loaded: false, shown: false, busy: false, evicted: false, failed: false, revealing: false, timer: null, rect: null, clip: null, probeTimer: null, repaired: false });
  const settingsRef = useRef(config?.settings);
  settingsRef.current = config?.settings;
  // Invité (lien partagé ouvert sans connexion) : aucune demande d'annonce → jamais de formulaire UMP avant la connexion.
  const userRef = useRef(user);
  userRef.current = user;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;
  const revealRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const s = st.current;
    const visibleRatio = placement === "reels" ? REELS_VISIBLE_RATIO : VISIBLE_RATIO;
    let alive = true;
    // Conteneur qui défile (Reels : le flux plein écran) → racine des observateurs, sinon l'approche
    // d'une slide encore sous le bord du conteneur ne serait jamais vue (recouvrement clippé par l'ancêtre).
    const root = scrollParent(el);
    // Lot 540 — cadre dans le repère que le plugin utilise : haut dans la fenêtre + défilement NATIF (celui que la vue
    // soustrait), pas window.scrollY. Sur Android, le flux plein écran des Reels est promu « root scroller » par Chromium :
    // la WebView rapporte alors SON scrollTop (window.scrollY reste 0) et la vue native le suit d'elle-même. Ainsi une
    // slide préchargée sous l'écran glisse avec le swipe jusqu'à sa place ; là où le défilement interne n'est pas suivi
    // (iOS), syncPosition() la déplace à la révélation. Le lot 524 (2.0.1) posait la vue à « root.top + décalage + 0 » :
    // sonde 2.1 « visible à y=-13963 (attendu 223), scrollY natif 14186 » = exactement -scrollTop.
    const absRect = async () => { const sy = await nativeScrollY(); const r = el.getBoundingClientRect(); return { x: r.left, y: Math.round(r.top + sy), width: r.width }; };
    // Bande visible de la fenêtre : entre les éléments fixes collés au bord haut (barre d'état, en-tête) et au bord
    // bas (barre du bas) — marqués data-native-occluder. La vue native dessinée par-dessus la WebView y est
    // découpée par le plugin, à chaque défilement natif. Un élément flottant au milieu relève de covered().
    const clipBand = () => {
      const r = el.getBoundingClientRect();
      let top = 0, bottom = window.innerHeight;
      document.querySelectorAll("[data-native-occluder]").forEach((o) => {
        const b = o.getBoundingClientRect();
        if (b.width === 0 || b.height === 0 || b.right <= r.left || b.left >= r.right) return;
        if (b.top <= 1) top = Math.max(top, b.bottom);
        else if (b.bottom >= window.innerHeight - 1) bottom = Math.min(bottom, b.top);
      });
      return { top: Math.round(top), bottom: Math.round(Math.max(top, bottom)) };
    };
    // Recouvert = l'élément au centre du cadre n'est pas le cadre (feuille, dialogue, visionneuse, menu…).
    const covered = () => {
      const r = el.getBoundingClientRect();
      if (r.width < 40 || r.height < 40) return true;
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      if (cy < 0 || cy > window.innerHeight || cx < 0 || cx > window.innerWidth) return true;
      const top = document.elementFromPoint(cx, cy);
      return !!top && top !== el && !el.contains(top);
    };
    const blocked = () => !s.visible || !enabledRef.current || document.visibilityState !== "visible" || covered();
    const conceal = () => {
      if (!s.shown) return;
      s.shown = false;
      setNativeAdVisible(owner, false);
      setState(s.loaded ? "loaded" : "idle");
    };
    const destroy = () => {
      clearTimeout(s.timer); s.timer = null; s.revealing = false;
      clearTimeout(s.probeTimer); s.probeTimer = null;
      const had = s.loaded || s.shown;
      s.loaded = false; s.shown = false; s.rect = null; s.clip = null;
      if (had) hideNativeAd(owner);
      setState("idle");
    };
    // La page a bougé depuis le chargement (image chargée au-dessus, slide arrivée) ou la bande visible a changé
    // (en-tête masqué…) → on déplace / redécoupe la vue.
    const syncPosition = async () => {
      const r = await absRect();
      if (!alive) return;
      const clip = clipBand();
      const moved = !s.rect || Math.abs(r.x - s.rect.x) > 1 || Math.abs(r.y - s.rect.y) > 1;
      const reclipped = !s.clip || Math.abs(clip.top - s.clip.top) > 1 || Math.abs(clip.bottom - s.clip.bottom) > 1;
      if (moved || reclipped) { s.rect = r; s.clip = clip; await moveNativeAd(owner, r, clip); }
    };
    // SONDE (2.0.1) : PROBE_DELAY_MS après la révélation, l'état RÉEL de la vue native est comparé au cadre. Vue absente,
    // invisible, découpée à zéro ou posée ailleurs → l'annonce est redemandée VISIBLE au cadre actuel (chemin direct,
    // sans préchargement), une seule fois par approche ; le détail remonte au diagnostic admin dans tous les cas.
    const verify = () => {
      clearTimeout(s.probeTimer);
      s.probeTimer = setTimeout(async () => {
        s.probeTimer = null;
        if (!alive || !s.shown || blocked()) return;
        const r = el.getBoundingClientRect();
        const res = await probeNativeAd(owner, { x: r.left, y: r.top }, placement);
        if (!alive || !res || res.ok || !s.shown || s.repaired) return;
        s.repaired = true;
        destroy();
        if (s.visible && s.near) loadNow();
      }, PROBE_DELAY_MS);
    };
    const reveal = () => {
      if (!alive || !s.loaded || s.shown || s.revealing || blocked()) return;
      s.revealing = true;
      const before = el.getBoundingClientRect().top;
      setTimeout(async () => {
        s.revealing = false;
        if (!alive || !s.loaded || s.shown || blocked()) return;
        if (Math.abs(el.getBoundingClientRect().top - before) > 1) return; // encore en mouvement → prochain tick
        await syncPosition();
        if (!alive || !s.loaded || s.shown || blocked()) return;
        s.shown = true; setState("shown");
        setNativeAdVisible(owner, true);
        verify();
      }, SETTLE_MS);
    };
    revealRef.current = reveal;
    const load = async () => {
      s.timer = null;
      if (!alive || s.busy || s.loaded || s.failed || s.evicted || !s.near || !userRef.current) return;
      // Cadre déjà visible → chargée VISIBLE d'un coup (prend la vue d'un emplacement qui ne fait que la garder
      // cachée) ; sinon préchargement invisible.
      const hidden = !s.visible;
      s.busy = true; setState("loading");
      const r = await absRect();
      if (!alive || !s.near) { s.busy = false; return; }
      const clip = clipBand();
      const res = await showNativeAd(settingsRef.current, placement, r, { owner, hidden, clip, el });
      s.busy = false;
      // Démonté ou parti loin pendant la demande → on ne garde jamais une vue orpheline (elle bloquerait
      // tous les autres emplacements, « busy » à l'infini).
      if (!alive || !s.near) { if (res && !res.busy) hideNativeAd(owner); return; }
      if (res?.busy) { setState("idle"); if (s.near && !s.timer) s.timer = setTimeout(load, BUSY_RETRY_MS); return; }
      if (!res) { s.failed = true; setState("failed"); onUnavailableRef.current?.(); return; }
      s.rect = r; s.clip = clip; s.loaded = true;
      if (hidden) { setState("loaded"); reveal(); return; }
      s.shown = true; setState("shown");
      await syncPosition();
      if (!alive) return;
      if (blocked()) conceal(); else verify();
    };
    const schedule = () => { if (s.timer) return; s.timer = setTimeout(load, SHOW_DELAY_MS); };
    // Visible sans vue (préchargement manqué, autre emplacement qui la tenait) → on (re)demande tout de suite.
    const loadNow = () => { clearTimeout(s.timer); s.timer = null; s.evicted = false; load(); };
    // Approche (~1 écran) → préchargement ; éloignement → destruction.
    const ioNear = new IntersectionObserver(([e]) => {
      const near = e.isIntersecting;
      if (near === s.near) return;
      s.near = near;
      if (near) { s.evicted = false; s.repaired = false; schedule(); } else destroy();
    }, { root, rootMargin: PRELOAD_MARGIN, threshold: 0 });
    // Visible → révélation ; sorti → cachée (toujours chargée).
    const ioVis = new IntersectionObserver(([e]) => {
      const vis = e.intersectionRatio >= visibleRatio;
      if (vis === s.visible) return;
      s.visible = vis;
      s.visibleSince = vis ? Date.now() : 0;
      if (!vis) { conceal(); return; }
      if (s.loaded) reveal();
      else if (!s.busy && !s.failed) loadNow();
    }, { root, threshold: [0, VISIBLE_RATIO, REELS_VISIBLE_RATIO, 1] });
    ioNear.observe(el);
    ioVis.observe(el);
    // Garde périodique : éviction par un autre emplacement, dérive de position, recouvrement, révélation en retard.
    const tick = setInterval(() => {
      if (!s.near) { if (s.loaded || s.shown) destroy(); return; }
      if ((s.loaded || s.shown) && nativeAdOwner() !== owner) { s.loaded = false; s.shown = false; s.evicted = true; s.revealing = false; s.rect = null; s.clip = null; setState("idle"); }
      if (s.loaded) {
        if (s.shown) { syncPosition(); if (blocked()) conceal(); } else reveal();
        return;
      }
      if (s.busy || s.failed) return;
      // Visible sans annonce (évincé, ou toujours « busy ») → on redemande sans attendre la prochaine entrée à l'écran.
      if (s.visible) {
        if (!s.timer || s.evicted) loadNow();
        if (s.visibleSince && Date.now() - s.visibleSince > STUCK_REPORT_MS) { s.visibleSince = 0; reportNativeStuck(placement, s); }
      } else if (!s.evicted && !s.timer) schedule();
    }, GUARD_MS);
    const onVis = () => { if (document.visibilityState !== "visible") conceal(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", destroy);
    return () => {
      alive = false;
      revealRef.current = null;
      ioNear.disconnect(); ioVis.disconnect(); clearInterval(tick);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", destroy);
      destroy();
    };
  }, [placement, owner]);

  // `enabled` retiré (reel plus actif) → cachée aussitôt (reste chargée) ; rendu → révélée sans attendre la garde.
  useEffect(() => {
    const s = st.current;
    if (enabled) { revealRef.current?.(); return; }
    if (s.shown) { s.shown = false; setNativeAdVisible(owner, false); setState(s.loaded ? "loaded" : "idle"); }
  }, [enabled, owner]);

  return { ref, state };
}

// Fausse annonce de la QA préversion (web) : même gabarit que le template Google « medium ».
const QaFakeAd = () => (
  <div className="h-full w-full flex flex-col p-3 gap-2 text-left" style={{ background: "#fff", color: "#111" }} data-testid="admob-qa-fake-ad">
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#FFCC66", color: "#7A4B00" }}>Ad</span>
      <div className="h-9 w-9 rounded-lg" style={{ background: "#3B82F6" }} />
      <div className="flex-1">
        <div className="text-sm font-bold">Test Ad : Google Ads</div>
        <div className="text-xs" style={{ color: "#555" }}>Stay up to date with your Ads Check how your ads are performing</div>
      </div>
    </div>
    <div className="flex-1 rounded-lg grid place-items-center text-xs font-semibold" style={{ background: "linear-gradient(135deg,#4285F4,#34A853 50%,#FBBC05)", color: "#fff" }}>Test Ad (media)</div>
    <div className="rounded-md py-2 text-center text-sm font-bold" style={{ background: "#4285F4", color: "#fff" }}>INSTALL</div>
  </div>
);

// Cadre de l'annonce : la vue native est dessinée exactement par-dessus. Squelette tant que Google n'a
// pas rendu l'annonce (les états sont exposés pour les tests : data-ad-state).
export const AdmobNativeBox = ({ slot, className = "", style, testId }) => (
  <div ref={slot.ref} className={`relative ${className}`} style={{ height: NATIVE_MEDIUM_HEIGHT, ...style }} data-testid={testId} data-ad-state={slot.state}>
    {slot.state !== "shown" && (
      <div className="absolute inset-0 p-3 flex flex-col gap-2 animate-pulse" aria-hidden>
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-white/10" />
          <div className="flex-1 space-y-1.5"><div className="h-3 w-2/3 rounded bg-white/10" /><div className="h-2.5 w-1/2 rounded bg-white/10" /></div>
        </div>
        <div className="flex-1 rounded-lg bg-white/10" />
        <div className="h-9 rounded-md bg-white/10" />
      </div>
    )}
    {slot.state === "shown" && admobQaMode() && <QaFakeAd />}
  </div>
);

// Carte « Annonce · Google » du Fil et du Profil (app native). Disparaît d'elle-même si Google n'a
// aucune annonce à fournir (jamais un cadre vide dans le fil).
export const AdmobNativeCard = ({ placement = "feed", routeKey = "" }) => {
  const { t } = useApp();
  const [gone, setGone] = useState(false);
  const slot = useAdmobNativeSlot({ placement, onUnavailable: () => setGone(true) });
  if (gone) return null;
  return (
    <div className="yalola-card overflow-hidden" data-testid={`admob-native-${placement}-${routeKey}`} data-ad-state={slot.state}>
      <div className="flex items-center gap-1.5 px-4 pt-3 pb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }} data-testid={`admob-native-label-${placement}-${routeKey}`}>
        <Megaphone size={12} /> {t("ads.native_google", "Annonce · Google")}
      </div>
      <AdmobNativeBox slot={slot} className="mx-3 mb-3 rounded-xl overflow-hidden" style={{ background: "rgba(0,0,0,0.06)" }} testId={`admob-native-box-${placement}-${routeKey}`} />
    </div>
  );
};

// Slide Google des Reels (format « native ») : fond sombre, badge « Annonce · Google », cadre de
// l'annonce au centre, invitation à glisser. L'annonce est PRÉCHARGÉE (invisible) dès que la slide
// approche, révélée quand la slide est active et en place, cachée dès qu'on la quitte. Sans annonce →
// onUnavailable(clé) → la slide est retirée du flux (souvent avant même d'y arriver) et le reel suivant
// prend sa place.
export const GoogleReelSlide = ({ item, active, onUnavailable }) => {
  const { t } = useApp();
  const slot = useAdmobNativeSlot({ placement: "reels", enabled: active, onUnavailable: () => onUnavailable?.(item._key) });
  return (
    <div className="reel-card" data-testid={`reel-google-ad-${item._key}`} data-ad-state={slot.state} data-active={active ? "1" : "0"}>
      <div className="reel-backdrop absolute inset-0 pointer-events-none" aria-hidden style={{ background: "radial-gradient(circle at 50% 30%, #222a38 0%, #12161f 55%, #090b10 100%)" }} />
      <div className="absolute inset-x-0 flex justify-center px-5" style={{ top: "calc(var(--sa-top) + 4.25rem)", bottom: "calc(var(--sa-bottom) + 3.5rem + 4.5rem)" }}>
        <div className="w-full max-w-[420px] flex flex-col justify-center">
          <div className="self-start inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide mb-2 text-white" style={{ background: "rgba(255,255,255,0.14)", backdropFilter: "blur(4px)" }} data-testid={`reel-google-ad-label-${item._key}`}>
            <Megaphone size={11} /> {t("ads.native_google", "Annonce · Google")}
          </div>
          <AdmobNativeBox slot={slot} className="w-full rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }} testId={`reel-google-ad-box-${item._key}`} />
        </div>
      </div>
      <div className="reel-caption absolute left-0 right-0 z-10 flex flex-col items-center text-white/80 pointer-events-none" data-testid={`reel-google-ad-hint-${item._key}`}>
        <ArrowDown size={18} className="animate-bounce" />
        <span className="text-xs font-semibold mt-1">{t("reels.swipe_to_continue", "Glissez pour continuer")}</span>
      </div>
    </div>
  );
};
